import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Seo from "@/components/Seo";
import ProductReviews from "@/components/ProductReviews";
import RecentlyViewed from "@/components/RecentlyViewed";
import { api, formatPrice, applyDiscount } from "@/lib/api";
import { pushRecentlyViewed } from "@/lib/recentlyViewed";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Truck, Package, ShoppingBag, Bookmark } from "lucide-react";
import { JerseyBackPreview } from "@/components/JerseyBackPreview";
import { SizeChart } from "@/components/SizeChart";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mockupUrl = (pid, garment, color) =>
  `${BACKEND}/api/mockup/${pid}?color=${encodeURIComponent(color)}&garment=${encodeURIComponent(garment || "Hoodie")}`;


// Pick the image that matches a garment by its URL (order isn't consistent across products).
const imgIdxForGarment = (images = [], garment = "") => {
  const wantShirt = garment.toLowerCase().includes("shirt");
  const idx = images.findIndex((im) => im.toLowerCase().includes(wantShirt ? "shirt" : "hoodie"));
  if (idx >= 0) return idx;
  // Fallback for products whose image URLs have no garment keyword (e.g. new drops):
  // by convention images[0] = hoodie, images[1] = shirt.
  if (wantShirt && images.length > 1) return 1;
  return 0;
};

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, saveItem, saved } = useCart();
  const { discountCents, garmentColors } = useSettings();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [variant, setVariant] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [selectedColor, setSelectedColor] = useState("Black");
  const [customName, setCustomName] = useState("");
  const [customNumber, setCustomNumber] = useState("");
  const [viewMode, setViewMode] = useState("front");

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products/${id}`)
      .then((r) => {
        const p = r.data;
        setProduct(p);
        pushRecentlyViewed(p);
        const enabled = p.variants?.filter((v) => v.is_enabled) || [];
        // Pick the garment that matches this product (title first, then display_price),
        // so a "Shirt" product opens on Shirt/$25 rather than defaulting to Hoodie/$45.
        const t = (p.title || "").toLowerCase();
        let primary;
        if (t.endsWith("shirt")) primary = "Shirt";
        else if (t.endsWith("hoodie")) primary = "Hoodie";
        else primary = (p.display_price ?? 9999) < 4000 ? "Shirt" : "Hoodie";
        const initial = enabled.find((v) => v.options?.garment === primary) || enabled[0] || null;
        setVariant(initial);
        setActiveImg(imgIdxForGarment(p.images, initial?.options?.garment || primary));
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center py-40 bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-40 bg-background">
        <p className="font-anton text-3xl uppercase text-white">Product not found</p>
        <Link to="/" className="text-primary underline mt-4 inline-block uppercase text-sm tracking-widest font-bold">Back to shop</Link>
      </div>
    );
  }

  const enabledVariants = product.variants?.filter((v) => v.is_enabled) || [];
  const isCustomizable = product.tags?.includes("customizable");
  const garments = [...new Set(enabledVariants.map((v) => v.options?.garment).filter(Boolean))];
  const hasGarments = garments.length > 0;
  const activeGarment = variant?.options?.garment || garments[0];
  const frontImg = mockupUrl(product.id, activeGarment, selectedColor);
  const sizesForGarment = hasGarments
    ? enabledVariants.filter((v) => v.options?.garment === activeGarment)
    : enabledVariants;

  const selectGarment = (g) => {
    const currentSize = variant?.options?.size;
    const match =
      enabledVariants.find((v) => v.options?.garment === g && v.options?.size === currentSize) ||
      enabledVariants.find((v) => v.options?.garment === g);
    if (match) setVariant(match);
    // switch the shown image to match the garment (matched by URL, order varies per product)
    if (product.images?.length > 1) {
      setActiveImg(imgIdxForGarment(product.images, g));
    }
  };

  const handleAdd = () => {
    if (!variant) return toast.error("Select a size first.");
    addItem(product, variant, { name: isCustomizable ? customName : "", number: isCustomizable ? customNumber : "", color: selectedColor });
    toast.success("Added to cart.");
  };

  const handleSave = () => {
    if (!variant) return toast.error("Select a size first.");
    saveItem(product, variant, { name: isCustomizable ? customName : "", number: isCustomizable ? customNumber : "", color: selectedColor });
    toast.success("Saved for later.");
  };

  const isSaved = variant && saved.some(
    (s) => s.product_id === product.id && s.variant_id === variant.id &&
      s.custom_color === selectedColor &&
      s.custom_name === (isCustomizable ? customName.trim() : "") &&
      s.custom_number === (isCustomizable ? customNumber.trim() : "")
  );

  const btnCls = (activeState) =>
    `px-6 py-3 text-sm font-bold uppercase border transition-colors ${
      activeState
        ? "bg-primary text-black border-primary"
        : "bg-transparent text-white border-white/20 hover:border-primary hover:text-primary"
    }`;

  const priceBase = variant ? variant.price : Math.min(...enabledVariants.map((v) => v.price));
  const priceSale = applyDiscount(priceBase, discountCents);
  const priceOnSale = discountCents > 0 && priceSale < priceBase;

  const seoTitle = `${product.title} — Custom Name & Number Hoodie & Tee | Jersey Blend`;
  const seoDesc = `${product.title}: original fan-made ${product.category} design on a premium hoodie or tee. Add your name and number on the back — 7 garment colors. Ships in 3–7 days.`;
  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.title,
    image: [frontImg],
    description: product.description || seoDesc,
    brand: { "@type": "Brand", name: "Jersey Blend" },
    category: product.category,
    offers: {
      "@type": "Offer",
      priceCurrency: "USD",
      price: (priceBase / 100).toFixed(2),
      availability: "https://schema.org/InStock",
      url: `https://jerseyblend.net/product/${product.id}`,
    },
  };

  return (
    <div className="bg-background pb-24 lg:pb-0">
      <Seo
        title={seoTitle}
        description={seoDesc}
        keywords={`${product.title}, custom ${product.category} hoodie, personalized ${product.title} jersey, ${product.title} name and number, custom hoodie with name`}
        path={`/product/${product.id}`}
        image={frontImg}
        jsonLd={productJsonLd}
      />
      <div data-testid="product-page" className="grid lg:grid-cols-2">
      {/* Images */}
      <div className="bg-neutral-900 p-6 md:p-12 lg:p-16 border-r border-white/10">
        <button
          data-testid="back-button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest mb-8 text-white/70 hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="aspect-[4/5] overflow-hidden bg-neutral-900 border border-white/10">
          {isCustomizable && viewMode === "back" ? (
            <div data-testid="pdp-back-view" className="w-full h-full">
              <JerseyBackPreview name={customName} number={customNumber} garment={activeGarment} className="h-full" />
            </div>
          ) : isCustomizable && viewMode === "full" ? (
            <div data-testid="pdp-full-view" className="w-full h-full grid grid-cols-2">
              <div className="relative border-r border-white/10">
                <img src={frontImg} alt={`${product.title} front`} className="w-full h-full object-contain" />
                <span className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/70 text-white/80 px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] font-bold">Front</span>
              </div>
              <div className="relative">
                <JerseyBackPreview name={customName} number={customNumber} garment={activeGarment} className="h-full" />
                <span className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/70 text-white/80 px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] font-bold">Back</span>
              </div>
            </div>
          ) : (
            <img data-testid="pdp-front-image" src={frontImg} alt={`${product.title} — ${selectedColor}`} className="w-full h-full object-contain" />
          )}
        </div>
        {isCustomizable && (
          <div className="grid grid-cols-3 gap-3 mt-4" data-testid="pdp-view-toggle">
            {[["front", "Front · Crest"], ["back", "Back · Name"], ["full", "Full Jersey"]].map(([mode, label]) => (
              <button
                key={mode}
                data-testid={`view-${mode}-button`}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] border transition-colors ${viewMode === mode ? "bg-primary text-black border-primary" : "bg-transparent text-white/70 border-white/20 hover:border-primary hover:text-primary"}`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Info (sticky) */}
      <div className="px-6 md:px-12 lg:px-16 py-12 lg:py-16">
        <div className="lg:sticky lg:top-28">
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-primary mb-4">{product.category}</p>
          <h1 className="font-anton text-4xl sm:text-5xl uppercase leading-none text-white">
            {product.title}
          </h1>
          {(() => {
            const base = variant ? variant.price : Math.min(...enabledVariants.map((v) => v.price));
            const sale = applyDiscount(base, discountCents);
            const onSale = discountCents > 0 && sale < base;
            return (
              <div className="mt-6 flex items-baseline gap-3">
                <p className="font-anton text-3xl text-primary" data-testid="pdp-price">{formatPrice(sale)}</p>
                {onSale && (
                  <>
                    <span className="font-anton text-xl text-white/40 line-through">{formatPrice(base)}</span>
                    <span data-testid="pdp-sale-badge" className="bg-primary text-black px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-bold">
                      {formatPrice(discountCents)} Off
                    </span>
                  </>
                )}
              </div>
            );
          })()}
          <p className="text-white/60 mt-6 leading-relaxed max-w-md">{product.description}</p>

          {/* Style */}
          {hasGarments && (
            <div className="mt-10">
              <p className="text-xs uppercase tracking-widest font-bold mb-3 text-white/70">Select Style</p>
              <div className="flex flex-wrap gap-3">
                {garments.map((g) => (
                  <button
                    key={g}
                    data-testid={`garment-${g.toLowerCase()}`}
                    onClick={() => selectGarment(g)}
                    className={btnCls(activeGarment === g)}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Color */}
          {garmentColors?.length > 0 && (
            <div className="mt-10" data-testid="color-selector">
              <p className="text-xs uppercase tracking-widest font-bold mb-3 text-white/70">
                Color: <span className="text-white">{selectedColor}</span>
              </p>
              <div className="flex flex-wrap gap-3">
                {garmentColors.map((c) => {
                  const active = selectedColor === c.name;
                  return (
                    <button
                      key={c.name}
                      data-testid={`color-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setSelectedColor(c.name)}
                      title={c.name}
                      aria-label={c.name}
                      className={`w-10 h-10 rounded-full border-2 transition-transform hover:scale-110 ${
                        active ? "border-primary scale-110" : "border-white/20"
                      }`}
                      style={{ backgroundColor: c.swatch }}
                    />
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-widest font-bold text-white/70">Select Size</p>
              <SizeChart hasYouth={garments.some((g) => g.toLowerCase().includes("youth"))} />
            </div>
            <div className="flex flex-wrap gap-3">
              {sizesForGarment.map((v) => (
                <button
                  key={v.id}
                  data-testid={`variant-${v.id}`}
                  onClick={() => setVariant(v)}
                  className={`min-w-[56px] ${btnCls(variant?.id === v.id)}`}
                >
                  {v.options?.size || v.title}
                </button>
              ))}
            </div>
          </div>

          {isCustomizable && (
            <div className="mt-10 border border-primary/30 bg-primary/5 p-5 space-y-4" data-testid="personalization-note">
              <div>
                <p className="text-xs uppercase tracking-widest font-bold mb-1 text-primary">Personalize your jersey</p>
                <p className="text-sm text-white/60">Add your <span className="text-white font-semibold">name &amp; number</span> — printed on the back. The embroidered crest stays on the front.</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-white/50">Name on back</label>
                  <input
                    data-testid="custom-name-input"
                    type="text"
                    maxLength={12}
                    value={customName}
                    onChange={(e) => { const v = e.target.value.toUpperCase(); setCustomName(v); setViewMode("full"); }}
                    placeholder="YOUR NAME"
                    className="mt-1.5 bg-transparent border border-white/20 focus:border-primary outline-none p-3 w-full text-white uppercase text-sm"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-white/50">Number</label>
                  <input
                    data-testid="custom-number-input"
                    type="text"
                    inputMode="numeric"
                    maxLength={2}
                    value={customNumber}
                    onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ""); setCustomNumber(v); setViewMode("full"); }}
                    placeholder="10"
                    className="mt-1.5 bg-transparent border border-white/20 focus:border-primary outline-none p-3 w-full text-white text-sm"
                  />
                </div>
              </div>

              {/* Live back preview */}
              <div data-testid="personalization-preview" className="mt-1">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-white/50">Back preview</p>
                  <button
                    data-testid="see-full-jersey-button"
                    onClick={() => setViewMode("full")}
                    className="text-[10px] uppercase tracking-widest font-bold text-primary hover:text-white transition-colors"
                  >
                    See it big ↑
                  </button>
                </div>
                <JerseyBackPreview name={customName} number={customNumber} garment={activeGarment} />
                <p className="text-[11px] text-white/40 mt-2 text-center">Use the <span className="text-white/70 font-semibold">Full Jersey</span> view above to see the front &amp; back together.</p>
              </div>
            </div>
          )}

          <button
            data-testid="add-to-cart-button"
            onClick={handleAdd}
            className="mt-8 w-full bg-primary text-black hover:bg-white px-8 py-5 font-bold uppercase tracking-[0.2em] text-sm transition-colors active:scale-95 flex items-center justify-center gap-3"
          >
            <ShoppingBag className="w-5 h-5" /> Add to cart
          </button>
          <button
            data-testid="save-for-later-button"
            onClick={handleSave}
            disabled={isSaved}
            className={`mt-3 w-full px-8 py-4 font-bold uppercase tracking-[0.2em] text-sm border transition-colors flex items-center justify-center gap-3 ${
              isSaved
                ? "border-primary/40 text-primary/70 cursor-default"
                : "border-white/20 text-white hover:border-primary hover:text-primary"
            }`}
          >
            <Bookmark className={`w-4 h-4 ${isSaved ? "fill-current" : ""}`} />
            {isSaved ? "Saved for later" : "Save for later"}
          </button>
          <p className="text-xs text-white/40 mt-3 text-center">
            Secure checkout with PayPal or card — no account needed.
          </p>

          <div className="mt-8 space-y-3 text-sm text-white/60">
            <div className="flex items-center gap-3"><Package className="w-4 h-4 text-primary" /> Printed on-demand, premium blanks</div>
            <div className="flex items-center gap-3"><Truck className="w-4 h-4 text-primary" /> Ships in 3–7 business days</div>
          </div>
        </div>
      </div>
      </div>
      <div className="max-w-6xl mx-auto w-full px-6 md:px-12 lg:px-16 pb-24">
        <ProductReviews productId={id} />
        <RecentlyViewed currentId={id} />
      </div>

      {/* Mobile sticky buy bar */}
      <div
        data-testid="sticky-buy-bar"
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 flex items-center gap-4 bg-black/90 backdrop-blur-xl border-t border-white/10 px-4 py-3"
      >
        <div className="flex flex-col leading-none">
          {priceOnSale ? (
            <>
              <span className="text-[11px] text-white/40 line-through">{formatPrice(priceBase)}</span>
              <span className="font-anton text-2xl text-primary">{formatPrice(priceSale)}</span>
            </>
          ) : (
            <span className="font-anton text-2xl text-white">{formatPrice(priceSale)}</span>
          )}
          {variant?.options?.size && (
            <span className="text-[10px] uppercase tracking-[0.15em] text-white/40 mt-1">
              {activeGarment} · {variant.options.size}
            </span>
          )}
        </div>
        <button
          data-testid="sticky-add-to-cart-button"
          onClick={handleAdd}
          className="flex-1 bg-primary text-black hover:bg-white px-6 py-4 font-bold uppercase tracking-[0.15em] text-sm transition-colors active:scale-95 flex items-center justify-center gap-2"
        >
          <ShoppingBag className="w-4 h-4" /> Add to cart
        </button>
      </div>
    </div>
  );
}
